/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import type { ContentReviewerAgent } from '../../hooks/useContentReviewerAgentResolver';

type FollowUpCardProps = {
	/**
	 * Agent whose avatar heads the card. Absent until the lookup lands, or for good if it can't
	 * be resolved — `AgentAvatar` falls back to its default avatar.
	 */
	agent?: ContentReviewerAgent;
	onDismiss?: () => void;
	onReview?: () => void;
};

const styles = cssMap({
	card: {
		position: 'relative',
		// Overlay surface — floats above the content, same as the suggestion card.
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		boxShadow: token('elevation.shadow.overlay'),
		width: '280px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		boxSizing: 'border-box',
	},
});

/**
 * Shown in place of the suggestion card once the last suggestion has been removed, offering to
 * generate suggestions for the rest of the page.
 */
export const FollowUpCard: React.ForwardRefExoticComponent<
	FollowUpCardProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, FollowUpCardProps>(function FollowUpCard(
	{ agent, onDismiss, onReview },
	ref,
): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div ref={ref} css={[styles.card]} data-testid={'ai-suggestion-follow-up-card'}>
			<Stack space="space.200">
				<Inline space="space.050" alignBlock="center">
					<AgentAvatar
						agentId={agent?.id}
						agentNamedId={agent?.external_config_reference ?? agent?.named_id}
						agentIdentityAccountId={agent?.identity_account_id}
						size="small"
						label={agent?.name ?? ''}
						showBorder={false}
					/>
					<Text size="large" weight="medium">
						{formatMessage(aiSuggestionsMessages.followUpCardTitle)}
					</Text>
				</Inline>
				<Text as="p" color="color.text">
					{formatMessage(aiSuggestionsMessages.followUpCardDescription)}
				</Text>
			</Stack>
			<Inline space="space.050" alignBlock="center">
				<Button iconBefore={TextReviewIcon} onClick={onReview}>
					{formatMessage(aiSuggestionsMessages.followUpCardReviewButtonLabel)}
				</Button>
				<Button appearance="subtle" onClick={onDismiss}>
					{formatMessage(aiSuggestionsMessages.cardDismissButtonLabel)}
				</Button>
			</Inline>
		</div>
	);
});
